<template>
    <div :class="[$style['mds-page-shell'], classObject]" :data-mds-version="version">
        <div :class="$style['mds-page-shell__header']">
            <!-- Skip Links -->
            <div :class="$style['mds-page-shell__skip-links']">
                <a href="#mds-page-shell-content" :class="$style['mds-page-shell__skip-link']">
                    {{ skipNavigationText }}
                </a>
            </div>

            <!-- Masthead and Horizontal Site Navigation -->
            <header :class="$style['mds-page-shell__masthead']">
                <div :class="$style['mds-page-shell__masthead-left']">
                    <template v-if="hiddenMenu">
                        <mds-button
                            @click="showHiddenNav()"
                            variation="icon-only"
                            icon="menu"
                            :class="[
                                $style['mds-masthead--hide-wide'],
                                $style['mds-page-shell__menu-button--responsive'],
                            ]"
                            :text="menuText"
                            type="button"
                            aria-haspopup="true"
                            :aria-expanded="navigationActive ? 'true' : 'false'"
                        ></mds-button>
                        <mds-button
                            v-if="!hiddenMenuHideWide"
                            @click="showHiddenNav()"
                            variation="flat"
                            flat-button-icon-size="medium"
                            icon="menu"
                            :class="[$style['mds-masthead--show-wide'], $style['mds-page-shell__menu-button']]"
                            :text="menuText"
                            type="button"
                            aria-haspopup="true"
                            :aria-expanded="navigationActive ? 'true' : 'false'"
                        ></mds-button>
                    </template>
                    <slot name="mds-page-shell-masthead-left"></slot>
                </div>
                <div :class="$style['mds-page-shell__masthead-logo-wrap']">
                    <component
                        :is="componentType"
                        :class="$style['mds-page-shell__masthead-link']"
                        :href="logoHref"
                        v-bind="routingProps"
                        v-if="logoHref || isRoutable"
                    >
                        <img :class="$style['mds-page-shell__masthead-logo-wide']" :src="logo" :alt="logoAlt" />
                        <img :class="$style['mds-page-shell__masthead-logo']" :src="logoResponsive" :alt="logoAlt" />
                    </component>
                    <template v-else>
                        <img :class="$style['mds-page-shell__masthead-logo-wide']" :src="logo" :alt="logoAlt" />
                        <img :class="$style['mds-page-shell__masthead-logo']" :src="logoResponsive" :alt="logoAlt" />
                    </template>
                </div>
                <div :class="$style['mds-page-shell__masthead-right']">
                    <slot name="mds-page-shell-masthead-right"></slot>
                </div>
            </header>

            <!-- Horizontal Site Navigation -->
            <div :class="$style['mds-page-shell__horizontal-nav']" v-if="hasHorizontalNav">
                <slot name="mds-page-shell-horizontal-nav"> </slot>
            </div>
        </div>

        <div :class="$style['mds-page-shell__body']">
            <!-- Vertical Site Navigation -->
            <div :class="$style['mds-page-shell__vertical-nav']" v-if="hasVerticalNav" ref="verticalNav">
                <slot name="mds-page-shell-vertical-nav"></slot>
            </div>

            <!-- Page Content -->
            <main :class="$style['mds-page-shell__content']" id="mds-page-shell-content">
                <slot></slot>
            </main>
        </div>

        <!-- Hidden Navigation -->
        <div
            :class="$style['mds-page-shell__hidden-nav-container']"
            v-if="$scopedSlots['mds-page-shell-hidden-nav']"
            ref="hiddenNav"
            v-overlay="{ visible: navigationActive, class: classes, handler: 'hideHiddenNav' }"
            :aria-hidden="navigationActive ? 'false' : 'true'"
        >
            <div :class="$style['mds-page-shell__hidden-nav-container-inner']">
                <div :class="$style['mds-page-shell__hidden-nav']">
                    <slot name="mds-page-shell-hidden-nav"></slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { MdsButton } from '@mds/button';
    import { MdsOverlay, MdsTrapFocus } from '@mds/utils-js';
    import MdsRoutable from '@mds/routable';

    const { name, version } = {
        name: "@mds/page-shell",
        version: "4.4.32"
    };

    export default {
        name: 'MdsPageShell',
        data() {
            return {
                navigationActive: false,
                classes: this.$style['mds-overlay'],
                version: `${name}-${version}`,
            };
        },
        props: {
            disableFocusTrap: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, suppresses the built-in focus trap focus allowing you to leverage your own or third-party focus management.',
                },
            },
            hiddenMenu: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, shows the button to reveal the hidden menu.',
                },
            },
            hiddenMenuHideWide: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, hides the flat button for the hidden menu on wide viewports.',
                },
            },
            logo: {
                type: String,
                required: true,
                docs: {
                    validation: '**Required**',
                    description: 'Image path for the masthead logo.',
                },
            },
            logoAlt: {
                type: String,
                required: true,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the `alt` attribute on the logo.',
                },
            },
            logoHref: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the `href` for the logo.',
                },
            },
            logoResponsive: {
                type: String,
                required: true,
                docs: {
                    validation: '**Required**',
                    description: 'Image path for the responsive masthead logo.',
                },
            },
            menuText: {
                type: String,
                default: 'Menu',
                docs: {
                    validation: '—',
                    description: 'Text for the hidden menu button.',
                },
            },
            skipNavigationText: {
                type: String,
                default: 'Skip to Content',
                docs: {
                    validation: '—',
                    description: 'Assists keyboard users to skip navigation and go directly to content.',
                },
            },
            sticky: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, makes header sticky. Note if vertical navigation is added, `sticky` is enabled automatically',
                },
            },
            tall: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets header tall.',
                },
            },
        },
        mixins: [MdsTrapFocus, MdsRoutable],
        directives: {
            overlay: MdsOverlay,
        },
        components: {
            MdsButton,
        },
        computed: {
            componentType() {
                return this.isRoutable ? this.linkTag : 'a';
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-page-shell--tall-header']] = this.tall;
                obj[this.$style['mds-page-shell--sticky-header']] = this.hasVerticalNav || this.sticky;

                obj[this.$style['mds-page-shell--horizontal-nav-active']] = this.hasHorizontalNav;
                obj[this.$style['mds-page-shell--vertical-nav-active']] = this.hasVerticalNav;
                obj[this.$style['mds-page-shell--hidden-nav-active']] = this.navigationActive;

                return obj;
            },
            hasVerticalNav() {
                return (
                    this.$scopedSlots['mds-page-shell-vertical-nav'] &&
                    this.$scopedSlots['mds-page-shell-vertical-nav']()?.length > 0
                );
            },
            hasHorizontalNav() {
                return (
                    this.$scopedSlots['mds-page-shell-horizontal-nav'] &&
                    this.$scopedSlots['mds-page-shell-horizontal-nav']()?.length > 0
                );
            },
        },
        methods: {
            showHiddenNav(event) {
                this.navigationActive = true;

                if (!this.disableFocusTrap) {
                    this.enableTrapFocus('hiddenNav');
                }

                this.$emit('mds-page-shell-hidden-nav-opened', event);
            },
            hideHiddenNav(event) {
                this.navigationActive = false;

                if (!this.disableFocusTrap) {
                    this.disableTrapFocus();
                }

                this.$emit('mds-page-shell-hidden-nav-dismissed', event);
            },
            bindEventHandlers() {
                document.addEventListener('keydown', this.keyDownEvents, true);
            },
            keyDownEvents(event) {
                if (event.keyCode === 27) {
                    this.hideHiddenNav();
                }
            },
        },
        mounted() {
            this.bindEventHandlers();
        },
        destroyed() {
            document.removeEventListener('keydown', this.keyDownEvents, true);
        },
    };
</script>

<style lang="scss" module>
    @import './page-shell.scss';
</style>
